import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import * as ProviderSelectors from 'redux/selectors/providers'
import withForm from 'hoc/withForm'
import TestIds from 'util/TestIds'
import FilterIcon from '@ui-components-3/icons/lib/regular/BarsFilter'
import CircleInfoIcon from '@ui-components-3/icons/lib/regular/CircleInfo'
import TherapyProviderFilters from '../TherapyProviderFilters/TherapyProviderFilters'
import { FiltersSchema } from 'schemas/NewVisitProviderSchema'
import {
  RaceFilterMap,
  availableTherapyVisitFiltersOptions,
  getBestMatchProviders,
  getCloseMatchProviders,
  getPrintableShortProviderName,
  getRemainingAvailableProviders,
  isTherapyRelated,
  mapTherapyVisitFiltersToDiagnosticExpertise,
  providerDefaultFilters,
} from 'util/provider'
import isEmpty from 'lodash/isEmpty'
import NonTherapyProviderFilters from '../NonTherapyProviderFilters'
import { getRequiredAriaLabel } from 'util/form'
import { phoneFormat } from 'util/formatters'
import { SUPPORT_NUMBER } from 'util/support'
import Loading from '@ui-components-3/ui/lib/components/Loading'
import ProviderInfoDialog from 'components/visit-creation/provider-info/ProviderInfoDialog'
import { clearProviders } from 'redux/slices/providers'
import { fetchAvailableProviders } from 'redux/thunks/providers'
import useId from '@ui-components-3/ui/lib/hooks/useId'
import { isSuccess } from 'util/helpers'
import useFeatureFlagToggle from 'components/feature-flag/useFeatureFlagToggle'
import { getVisitProviderIds } from 'redux/selectors/visits'
import { setOrUpdateFilters, setSelectedCategory } from 'redux/slices/providerFilters'
import { TcFF } from 'components/feature-flag/flags'
import { getMemberAge } from 'redux/selectors/members'
import { useMinorConsent } from 'hooks/useMinorConsent'
import { useTranslation } from 'react-i18next'
import { useHasOperatingHours } from 'hooks/operating-hours/useHasOperatingHours'
import { orderBy } from 'lodash'
import { RECOMMENDED_PROVIDER_COUNT } from 'util/constants'
import ProviderCards from './ProviderCards'

// TODO - convert this file to typescript and fix the errors that come up, move filtering and sorting logic to a separate hook
export const ProviderSelectionForm = withForm((props) => {
  const { isValid, handleSubmit, setFieldValue, pendingVisit, values } = props
  const { t } = useTranslation()

  const successVisit = useMemo(() => {
    return isSuccess(pendingVisit.visitType)
  }, [pendingVisit])

  const hasOperatingHours = useHasOperatingHours()

  const dispatch = useDispatch()

  const [isMinorExpertNeeded, setIsMinorExpertNeeded] = useState(false)
  const { getConsentTypeAndStatus } = useMinorConsent(pendingVisit.memberId)

  const isMinorConsentPhase2FlagOn = useFeatureFlagToggle(TcFF.tcMinorConsentPhase2)
  const isServiceLineRoutingFlagOn = useFeatureFlagToggle('tcServiceLineRouting')
  const memberAge = useSelector(getMemberAge(pendingVisit?.memberId))

  const shouldSeeMinorExpertProviders =
    isMinorConsentPhase2FlagOn && !isServiceLineRoutingFlagOn && memberAge && memberAge < 18

  const getConsentData = useCallback(async () => {
    const { isSelfConsentGranted, isParentConsentGranted } = await getConsentTypeAndStatus(
      pendingVisit.visitType,
      pendingVisit.location,
    )

    setIsMinorExpertNeeded(isSelfConsentGranted && !isParentConsentGranted)
  }, [getConsentTypeAndStatus, pendingVisit.visitType, pendingVisit.location])

  const { memberId, visitType, location, locationCountry } = pendingVisit || {}
  const isPendingVisitEmpty = useMemo(() => isEmpty(pendingVisit), [pendingVisit])

  const handleFetchAvailableProviders = useCallback(
    async (filters) => {
      if (isPendingVisitEmpty) return null
      dispatch(clearProviders())

      return dispatch(
        fetchAvailableProviders({
          memberId,
          visitType,
          location,
          filters,
          locationCountry,
          isMinorSpecialtyNeeded: isMinorExpertNeeded,
          afterHours: !!hasOperatingHours,
        }),
      )
    },
    [
      dispatch,
      hasOperatingHours,
      isMinorExpertNeeded,
      isPendingVisitEmpty,
      location,
      locationCountry,
      memberId,
      visitType,
    ],
  )

  const allAvailableProviders = useSelector(ProviderSelectors.getAvailableProviders(pendingVisit))

  const allVisits = useSelector(getVisitProviderIds)

  const [expandedProvider, setExpandedProvider] = useState(null)
  const providerInfoDialogRef = useRef(null)
  const [isFiltersVisible, setIsFiltersVisible] = useState(false)
  const filterRef = useRef(null)
  const isTcProviderFilterV2FlagOn = useFeatureFlagToggle('tcProviderFilterV2')
  const toggleIsFiltersVisible = useCallback((_isVisible) => () => setIsFiltersVisible(!!_isVisible), [])
  const moreThanSevenProviders = useMemo(() => allAvailableProviders.length > 7, [allAvailableProviders])

  const canFilterTherapy = useMemo(() => isTherapyRelated(pendingVisit), [pendingVisit])

  const selectedValuesCount = useMemo(() => {
    if (canFilterTherapy) {
      const hasGenderFilter = values.filters['gender']?.length > 0 ? 1 : 0
      const specialtyValues = values.filters?.specialtyFocus || {}
      const expertiseFilterNumb = Object.values(specialtyValues)?.filter(Boolean)?.length
      const raceFilterNumb = values.filters['race']?.length > 0 ? 1 : 0

      return hasGenderFilter + expertiseFilterNumb + raceFilterNumb
    }

    const countTrueValues = (obj) => Object.values(obj).filter(Boolean).length

    return Object.keys(values.filters).reduce((acc, key) => {
      return acc + countTrueValues(values.filters[key])
    }, 0)
  }, [canFilterTherapy, values.filters])

  const shouldShowFilterUi = useMemo(() => {
    return canFilterTherapy && isTcProviderFilterV2FlagOn && moreThanSevenProviders
  }, [canFilterTherapy, isTcProviderFilterV2FlagOn, moreThanSevenProviders])

  const allSortedProviders = useMemo(() => {
    if (!allAvailableProviders?.length) return []

    const sorters = [
      {
        fn: (item) => (item.hasW2Priority ? 1 : 0),
        order: 'desc',
      },
    ]

    if (selectedValuesCount && shouldShowFilterUi) {
      sorters.push({
        fn: (item) => item.recommendPriority || 0,
        order: 'desc',
      })
    }

    // move providers without availableNext to the bottom
    sorters.push({
      fn: (item) => (item.schedule?.availableNext ? 1 : 0),
      order: 'desc',
    })

    sorters.push({
      fn: (item) => item.schedule?.availableNext || '',
      order: 'asc',
    })

    return orderBy(
      allAvailableProviders,
      sorters.map((item) => item.fn),
      sorters.map((item) => item.order),
    )
  }, [allAvailableProviders, selectedValuesCount, shouldShowFilterUi])

  const previousProviders = useMemo(
    () => allSortedProviders.filter((provider) => allVisits.includes(provider.providerId)),
    [allSortedProviders, allVisits],
  )

  const availableProviderExpertise = useMemo(() => {
    const allProviderExpertise = allSortedProviders
      .map((provider) => provider.profile?.diagnosticExpertise)
      .filter(Boolean)
    return Array.from(new Set(allProviderExpertise.flat()))
  }, [allSortedProviders])

  const availableProviderGenders = useMemo(() => {
    const genderOptions = ['Male', 'Female', 'Another Gender Identity']

    const availableGenders = new Set(
      allSortedProviders.map((provider) => {
        const gender = provider?.profile?.genderIdentity
        return genderOptions.includes(gender) ? gender : 'Another Gender Identity'
      }),
    )

    availableGenders.add('All')

    return Array.from(availableGenders)
  }, [allSortedProviders])

  const availableProviderEthnicity = useMemo(() => {
    const allProviderRace = allSortedProviders
      .map((provider) => provider.profile?.raceEthnicity)
      .filter((race) => RaceFilterMap.some((filter) => filter.value === race))

    const flattenedProviderRaces = Array.from(new Set(allProviderRace.flat()))
    const availableRaceFilterTitles = RaceFilterMap.filter((race) => flattenedProviderRaces.includes(race.value)).map(
      (race) => race.label,
    )
    return availableRaceFilterTitles.push('All') && availableRaceFilterTitles
  }, [allSortedProviders])

  const checkConditions = useMemo(() => {
    const presentConditions = []
    availableProviderExpertise.forEach((condition) => {
      const conditionFound = Object.values(mapTherapyVisitFiltersToDiagnosticExpertise).some((expertiseArray) =>
        expertiseArray.includes(condition || ''),
      )
      if (conditionFound && condition) {
        presentConditions.push(condition)
      }
    })
    return presentConditions
  }, [availableProviderExpertise])

  const availableExpertiseFilters = useMemo(
    () => availableTherapyVisitFiltersOptions(checkConditions),
    [checkConditions],
  )

  const hideFilterOption = useCallback(
    (option) => {
      if (option === 'Expertise') {
        return availableExpertiseFilters.length < 3
      } else if (option === 'Gender') {
        return availableProviderGenders.length < 1
      } else if (option === 'Race') {
        return availableProviderEthnicity.length < 3
      }
      return false
    },
    [availableExpertiseFilters, availableProviderEthnicity, availableProviderGenders],
  )

  const bestMatchResults = useMemo(
    () => getBestMatchProviders(allSortedProviders, values.filters),
    [allSortedProviders, values.filters],
  )

  const closeMatchResults = useMemo(
    () => getCloseMatchProviders(allSortedProviders, values.filters, bestMatchResults),
    [allSortedProviders, bestMatchResults, values.filters],
  )

  const remainingAvailableResults = useMemo(
    () => getRemainingAvailableProviders(allSortedProviders, bestMatchResults, closeMatchResults),
    [allSortedProviders, bestMatchResults, closeMatchResults],
  )

  const defaultFilters = providerDefaultFilters(pendingVisit)

  useEffect(() => {
    if (shouldSeeMinorExpertProviders) {
      getConsentData()
    }
  }, [getConsentData, shouldSeeMinorExpertProviders])

  useEffect(() => {
    handleFetchAvailableProviders()
  }, [handleFetchAvailableProviders])

  const getProviderFilterCategory = useCallback(
    (providerId) => {
      if (!selectedValuesCount) {
        return 'Available'
      }
      if (bestMatchResults.some((provider) => provider.providerId === providerId)) {
        return 'Best Match'
      }
      if (closeMatchResults.some((provider) => provider.providerId === providerId)) {
        return 'Close Match'
      }
      return 'Additional Providers'
    },
    [bestMatchResults, closeMatchResults, selectedValuesCount],
  )

  const handleProviderSelection = (providerId) => {
    setFieldValue('providerId', providerId)
    const filterCategorySelectedFrom = getProviderFilterCategory(providerId)
    dispatch(setSelectedCategory(filterCategorySelectedFrom))
    providerInfoDialogRef.current?.close()
    setExpandedProvider(null)
  }

  const handleProviderDetailsClick = useCallback(
    (provider) => (e) => {
      e.stopPropagation()
      if (!expandedProvider || expandedProvider.providerId !== provider.providerId) {
        setExpandedProvider(provider)
        providerInfoDialogRef.current?.open()
      }
    },
    [setExpandedProvider, expandedProvider],
  )

  useEffect(() => {
    setTimeout(() => {
      if (!values.providerId) {
        if (values.providerName) {
          setFieldValue('providerName', undefined)
        }
      } else {
        if (allSortedProviders.length) {
          const provider = allSortedProviders.find((provider) => provider.providerId === values.providerId)
          if (provider) {
            setFieldValue('providerName', getPrintableShortProviderName(provider, successVisit))
            setFieldValue('providerTimezone', provider.timezone)
          } else {
            console.error('no provider found')
          }
        }
      }
    }, 100)
  }, [allSortedProviders, setFieldValue, successVisit, values.providerId, values.providerName])

  const clearFilters = () => {
    setFieldValue('filters', defaultFilters)
    handleFetchAvailableProviders()
    toggleIsFiltersVisible(false)()
  }

  const applyFilters = (filters) => {
    setFieldValue('filters', filters)
    dispatch(setOrUpdateFilters(filters))
    toggleIsFiltersVisible(false)()
  }

  const RenderFilters = useMemo(
    () => (pendingVisit && canFilterTherapy ? TherapyProviderFilters : NonTherapyProviderFilters),
    [canFilterTherapy, pendingVisit],
  )

  const filterButtons = [
    <button
      key="filter-btn"
      onClick={toggleIsFiltersVisible(true)}
      className={`btn-small w-36 rounded-xl ${
        selectedValuesCount || isFiltersVisible ? 'btn-neutral' : 'btn-primary-outlined'
      }`}
      type="button"
    >
      <FilterIcon className="h-5 w-5" aria-hidden="true" />
      {t('providerSelection.filter')} {!!selectedValuesCount && `(${selectedValuesCount})`}
    </button>,
  ]

  const idPrefix = useId()
  const allProvidersLabelId = idPrefix + 'all'
  const previousProvidersLabelId = idPrefix + 'previous'
  const availableProvidersLabelId = idPrefix + 'available'
  const recommendedProvidersLabelId = idPrefix + 'recommended'
  const bestMatchProviderLabelId = idPrefix + 'best_match'
  const closeMatchProviderLabelId = idPrefix + 'close_match'
  const remainingAvailableProviderLabelId = idPrefix + 'remaining_available'

  const sectionedByRecommendedProviders = useMemo(
    () =>
      allSortedProviders.reduce(
        (acc, curr) => {
          if (acc.recommended.data.length < RECOMMENDED_PROVIDER_COUNT && curr.hasW2Priority) {
            acc.recommended.data.push(curr)
          } else {
            acc.available.data.push(curr)
          }

          return acc
        },
        {
          recommended: {
            data: [],
            title: t('providerSelection.bestMatch.title'),
            labelId: recommendedProvidersLabelId,
          },
          available: {
            data: [],
            title: t('providerSelection.available'),
            labelId: availableProvidersLabelId,
          },
        },
      ),
    [allSortedProviders, availableProvidersLabelId, recommendedProvidersLabelId, t],
  )

  const allSections = useMemo(() => {
    let _sections = [
      canFilterTherapy
        ? {
            title: t('providerSelection.previousProviders'),
            data: previousProviders ?? [],
            labelId: previousProvidersLabelId,
            isHidden: !!selectedValuesCount && (!!bestMatchResults?.length || !!closeMatchResults?.length),
          }
        : null,
    ]

    if (shouldShowFilterUi) {
      _sections = [
        ..._sections,
        {
          title: t('providerSelection.bestMatch.title'),
          description: t('providerSelection.bestMatch.description'),
          data: bestMatchResults ?? [],
          labelId: bestMatchProviderLabelId,
          isHidden: !selectedValuesCount,
        },
        {
          title: t('providerSelection.closeMatch.title'),
          description: t('providerSelection.closeMatch.description'),
          data: closeMatchResults ?? [],
          labelId: closeMatchProviderLabelId,
          isHidden: !selectedValuesCount,
        },
        {
          title: t('providerSelection.additionalProviders'),
          data: remainingAvailableResults ?? [],
          labelId: remainingAvailableProviderLabelId,
          isHidden: !selectedValuesCount,
        },
        {
          title: t('providerSelection.available'),
          data:
            Object.values(sectionedByRecommendedProviders).reduce((acc, curr) => {
              return [...acc, ...curr.data]
            }, []) ?? [],
          labelId: remainingAvailableProviderLabelId,
          isHidden: !!selectedValuesCount,
        },
      ]
    } else {
      _sections = [..._sections, ...Object.values(sectionedByRecommendedProviders)].filter(Boolean)
    }

    return _sections.filter((i) => i && !i?.isHidden && !!i?.data?.length)
  }, [
    bestMatchProviderLabelId,
    bestMatchResults,
    canFilterTherapy,
    closeMatchProviderLabelId,
    closeMatchResults,
    previousProviders,
    previousProvidersLabelId,
    remainingAvailableProviderLabelId,
    remainingAvailableResults,
    sectionedByRecommendedProviders,
    selectedValuesCount,
    shouldShowFilterUi,
    t,
  ])

  return (
    <>
      <form className="flex w-full flex-1 flex-col" onSubmit={handleSubmit}>
        <div className="max-w-[1180px] pb-6">
          <div className="mb-4 flex flex-col justify-between md:flex-row">
            <div className="mb-2 shrink-0">
              <h2
                className="typography-h5 m-0 text-xl text-neutral-800"
                data-testid={TestIds.newVisit.label.providerTitle}
                id={allProvidersLabelId}
              >
                {successVisit ? t('providerSelection.title.coach') : t('providerSelection.title.provider')}
              </h2>
            </div>
            {!!shouldShowFilterUi && <div className="flex flex-wrap items-center">{filterButtons}</div>}
          </div>
          {!!selectedValuesCount && (
            <div className="mb-8 flex justify-between border-b-[1px] border-b-neutral-300 pb-2">
              <span className="typography-h5 text-neutral-800">{t('providerSelection.results')}</span>
              <button
                type="button"
                className="typography-body-l text-primary-600 btn-primary-borderless btn-sm pr-1"
                onClick={() => {
                  filterRef.current?.resetForm()
                  clearFilters()
                }}
              >
                {t('providerSelection.clearFilter')}
              </button>
            </div>
          )}

          {allSortedProviders.length === 0 ? (
            props.isLoading ? (
              <div className="flex justify-center">
                <Loading>
                  <p className="typography-body-l text-neutral">{t('providerSelection.loading')}</p>
                </Loading>
              </div>
            ) : (
              <p className="typography-body-l text-neutral">{t('providerSelection.noMatches')}</p>
            )
          ) : (
            <div className="flex flex-col" aria-label={getRequiredAriaLabel('Provider')}>
              {allSections.map(({ title, description, labelId, data }, index) => (
                <div key={labelId}>
                  {!!title && !!(allSections.length > 1 || labelId !== availableProvidersLabelId) && (
                    <div className="relative flex items-center">
                      <div className="typography-body font-medium uppercase text-neutral-500" id={labelId}>
                        {title}
                      </div>
                      {!!description && (
                        <div className="group relative">
                          <CircleInfoIcon className="text-primary-600 mb-1 ml-2 cursor-pointer" aria-hidden="true" />
                          <div className="typography-body-l absolute bottom-full left-0 z-10 mb-2 hidden w-max rounded-md bg-black p-4 font-medium text-white group-hover:block">
                            {description}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="pb-6">
                    <ProviderCards
                      labelId={labelId}
                      data={data}
                      handleProviderSelection={handleProviderSelection}
                      onDetailsClick={handleProviderDetailsClick}
                      selectedProviderId={values.providerId}
                      indexOffset={index ? allSections[index - 1]?.data?.length || 0 : 0}
                      isSuccess={successVisit}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {!successVisit && (
          <div className="typography-body mb-6 font-medium text-neutral-800">
            {t('providerSelection.needHelp.text')}{' '}
            <a className="text-link-primary font-semibold" href={`tel:+${SUPPORT_NUMBER}`}>
              {phoneFormat(SUPPORT_NUMBER)}
            </a>{' '}
            {t('providerSelection.needHelp.connect')}
          </div>
        )}
        {props.children({ disabled: !isValid || !values.providerId })}
      </form>
      <ProviderInfoDialog
        ref={providerInfoDialogRef}
        onSelect={() => handleProviderSelection(expandedProvider?.providerId)}
        provider={expandedProvider}
        isSuccess={successVisit}
        onClose={() => setExpandedProvider(null)}
      />
      {!!shouldShowFilterUi && (
        <RenderFilters
          onCancel={toggleIsFiltersVisible(false)}
          onSubmit={applyFilters}
          initialValues={values.filters || defaultFilters}
          schema={FiltersSchema}
          isVisible={isFiltersVisible}
          filterRef={filterRef}
          hideFilterOption={hideFilterOption}
          availableExpertise={availableExpertiseFilters}
          availableGenders={availableProviderGenders}
          availableEthnicity={availableProviderEthnicity}
          defaultFilters={defaultFilters}
          selectedCount={selectedValuesCount}
        />
      )}
    </>
  )
})

ProviderSelectionForm.propTypes = {
  initialValues: PropTypes.object.isRequired,
  schema: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  values: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  enableReinitialize: PropTypes.bool,
  dirty: PropTypes.bool,
  isValid: PropTypes.bool,
  isLoading: PropTypes.bool,
  pendingVisit: PropTypes.object,
  children: PropTypes.func.isRequired,
}

export default ProviderSelectionForm
